import { Location } from '@core_n/common/Location';
import { MsgHandler } from '@core_n/msg/msgHandler';
import * as projConsts from '../common/projConsts';
import { ConnectNotify, addConnectNotifier } from '@core_n/connect/connect-dialog.component';
import { Consts } from '@core_n/coordinates/Consts';

export interface SensorBias {

  /**
  * The ID of the external system for which the values are relevant.
  * Range: [0..]
  * Mandatory field.
  */
  systemId: number;
  /**
  * Shift in the position (longitude, latitude and altitude)
  * Units: lon/lat in Radians, altitude in Meters
  * Optional field. Null value: all fields will contain 0
  */
  shifting?: Location;
  /**
  * Bias in range.
  * Range: [-10^8..10^8], Units: Meters
  * Optional field. Null value: 0
  */
  range?: number;
  /**
  * Range bias accuracy.
  * Range: [0..10^8], Units: Meters
  * Optional field. Null value: 0
  */
  rangeAccuracy?: number;
  /**
  * Bias in azimuth.
  * Increases clockwise (N->E->S->W)
  * Range: [-2PI..2PI], Units: Radians
  * Optional field. Null value: 0
  */
  azimuth?: number;
  /**
  * Azimuth bias accuracy.
  * Range: [0..2PI], Units: Radians
  * Optional field. Null value: 0
  */
  azimuthAccuracy?: number;
  /**
  * Bias in elevation.
  * Range: [-PI/2..PI/2], Units: Radians
  * Optional field. Null value: 0
  */
  elevation?: number;
  /**
  * Elevation bias accuracy.
  * Range: [0..PI], Units: Radians
  * Optional field. Null value: 0
  */
  elevationAccuracy?: number;
  /**
  * Location confidence.
  * True - indicates that the location of the external system is correct, False - otherwise.
  * Mandatory field.
  */
  isWellLocated: boolean;
  /**
  * This field indicates whether the azimuth of the external system is registered with the current bias values
  * Mandatory field.
  */
  isAzimuthRegistered: boolean;
  /**
  * This field indicates whether the range of the external system is registered with the current bias values
  * Mandatory field.
  */
  isRangeRegistered: boolean;
  /**
  * This field indicates whether the elevation of the external system is registered with the current bias values
  * Mandatory field.
  */
  isElevationRegistered: boolean;
  /**
  * This field indicates whether the location of the external system is registered with the current bias values
  * Mandatory field.
  */
  isLocationRegistered: boolean;
}

export class SensorBiasMngr extends MsgHandler implements ConnectNotify {

  private static _self: SensorBiasMngr = null;
  private sensorsBiases: Map<number, SensorBias> = new Map();

  public static getSelf(): SensorBiasMngr {
    if (this._self == null)
      this._self = new SensorBiasMngr();
    return this._self;
  }

  private constructor() {
    super(-1);
    addConnectNotifier(this);
  }
   
  getSensorBias(sensorId: number): SensorBias {
    return this.sensorsBiases.get(sensorId);
  }

  handleMsg(msgObj: any): boolean {
    if (msgObj.msgType == projConsts.MSG_TYPE_SENSOR_BIAS) {
      let sensorBias: SensorBias = msgObj;
      this.sensorsBiases.set(sensorBias.systemId, sensorBias);
      return true;
    }
    return false;
  }

  onConnect() {
    this.sensorsBiases.clear();
  }
}
